/**
 * A nav bar that blends into the hero until the page moves.
 *
 * The bar lies transparent on the picture and becomes the ordinary bar --
 * background, ink and mark -- from the moment the page has moved. All this
 * file does is add one class, `is-at-top`; every colour, the scrim and the two
 * marks are CSS.
 *
 * The class adds the *hero* look, and the stylesheet's own answer is the
 * standard opaque bar. That order matters more than anything else here. It
 * used to be the other way round -- transparent by default, a class to make it
 * solid -- so any surface that did not run this file left the bar transparent
 * for ever with the page scrolling through it. The template preview never ran
 * it, and that is exactly what it showed. A surface that runs nothing now
 * shows an ordinary bar: the wrong one of two right answers, not a broken one.
 *
 * Asked for only by the one combination that changes: blended *and* kept
 * visible. A blended bar that scrolls away with the picture never changes,
 * and neither does a solid bar that stays at the top, so neither asks for
 * this file. `blocks/navbar_state.py` is where that rule lives; the page
 * carries the marker this looks for only when it says so.
 *
 * Why a script at all, on a site that otherwise runs none: CSS can do this
 * with a scroll-driven animation, and Firefox has no support for one at all
 * -- it is the reason the feature is not Baseline. A bar that does nothing in
 * one major browser is a support question nobody can answer.
 *
 * ## What is measured, and why it is not the scroll position
 *
 * The first version asked which element scrolls and read its `scrollTop`. It
 * looked for an ancestor with `overflow-y: auto` that was taller than its own
 * box, and in the builder it ran before the photographs had loaded -- so the
 * canvas was not yet taller than its box, no scroller was found, the bar fell
 * back to watching the window, and the window in the builder never moves. The
 * answer was then cached for the life of the bar, so it never recovered. The
 * bar stayed transparent over the page for ever, which is the bleed.
 *
 * So nothing is asked about scrolling at all. The bar is drawn at the top of
 * whatever contains it; the block it came from stays where it was in the
 * flow. Comparing the two says whether the page has moved under the bar, and
 * it says it the same way on a published page, in the builder's canvas, and
 * inside anything else that scrolls. Geometry, rather than a guess about
 * which box the scrollbar belongs to.
 */

/** How far the flow moves under the bar before it stops being at the top. */
const THRESHOLD = 8;

function watchBlendedBars(): void {
  const bars = Array.from(
    document.querySelectorAll<HTMLElement>("[data-navbar-blend]"),
  );
  if (bars.length === 0) return;

  for (const bar of bars) {
    // Once per bar. In the builder the canvas is rebuilt as blocks are added
    // and moved, and this runs again each time -- a bar bound twice would
    // carry two listeners doing the same work.
    if (bar.dataset.navbarWatched === "1") continue;

    // Where the bar's block sits in the flow. It is still in the document
    // even though the bar has left it: a box of no height, at the place the
    // bar would have occupied.
    const inFlow = bar.parentElement;
    if (!inFlow) continue;
    bar.dataset.navbarWatched = "1";

    // The last state written, so a scroll that changes nothing touches no
    // classes. Scrolling fires constantly, and a class written on every event
    // is a style recalculation on every event.
    let atTop: boolean | null = null;

    const apply = (): void => {
      // Both read in the same frame, so they describe the same moment.
      const moved =
        inFlow.getBoundingClientRect().top <
        bar.getBoundingClientRect().top - THRESHOLD;
      if (moved === atTop) return;
      atTop = moved;
      // The class adds the hero look; the stylesheet's own answer is the
      // standard bar. A surface that never runs this shows an ordinary opaque
      // bar rather than a transparent one with the page scrolling through it.
      bar.classList.toggle("is-at-top", !moved);
    };

    let queued = false;
    const onScroll = (): void => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        apply();
      });
    };

    // Once, straight away. This file is deferred, so it runs after the markup
    // is parsed and before the first paint -- which is why the hero look is
    // there from the start rather than flashing in over a standard bar. It
    // also settles a page opened at an anchor, which is already past the top
    // and should not be wearing the hero look at all.
    apply();

    // Captured on the document rather than bound to one element. A scroll
    // event does not bubble, so a listener on the window never hears the
    // canvas scrolling -- but the capture phase reaches every one of them,
    // whichever box the scrollbar turns out to belong to.
    document.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", onScroll, { passive: true });
  }
}

/**
 * 2.192.0. Whether a one-page site's row fits.
 *
 * Kelly: "any element that does not fit on a Mobile view, should be folded
 * into a hamburger menu. Even if the site is a single page ... Normally on a
 * single page site, there is no hamburger, but it should come back if needed
 * for any elements that don't fit on the screen." A bar with menu links, a
 * search box or further buttons folds everything on a phone by rule; a bar
 * with only a number or a button beside its brand (`data-navbar-fit`) keeps
 * them on the row while they fit. Whether they fit is the brand's length, the
 * number and the button's words and the font, so it is measured here, with
 * the row as it would be unfolded:
 *
 * - the row is wider than its box, or
 * - the brand's own content spills out of its box (the brand shrinks before
 *   anything else, and its words then run under the number -- the overlap
 *   Kelly photographed), or
 * - the number and the button spill out of theirs.
 *
 * `navbar--crowded` then hides them on the row and shows the hamburger and
 * the folded copy. The stylesheet does that only on a phone, so where the
 * folded copy still is not shown -- a wide screen -- the class comes off
 * again rather than leaving a hamburger that opens nothing.
 */
const CROWDED = "navbar--crowded";

export function spills(element: Element | null): boolean {
  if (!element) return false;
  return element.scrollWidth > element.clientWidth + 1;
}

export function measureFit(bar: HTMLElement): boolean {
  bar.classList.remove(CROWDED);
  // 2.203.0. The phone's row, which is the one a phone shows.
  const row = bar.querySelector(".navbar__row--phone");
  const brand = bar.querySelector(".navbar__row--phone > .navbar__brand");
  const actions = bar.querySelector(".navbar__row--phone > .navbar__actions");
  // And the brand's words. The rebuilt brand is a flex box, so its text
  // shrinks to an ellipsis inside it rather than spilling out of it, and
  // a name cut short is a row that does not fit. (The brand was a block
  // until 2.203.0 and its text spilled; navbar_fit.spec.ts found the
  // difference.)
  const words = bar.querySelector(".navbar__row--phone > .navbar__brand .navbar__brand-text");
  const crowded = spills(row) || spills(brand) || spills(actions) || spills(words);
  if (crowded) {
    bar.classList.add(CROWDED);
    const folded = bar.querySelector<HTMLElement>(".navbar__item--folded");
    if (!folded || getComputedStyle(folded).display === "none") {
      bar.classList.remove(CROWDED);
    }
  }
  return bar.classList.contains(CROWDED);
}

function watchCrowdedBars(): void {
  const bars = Array.from(document.querySelectorAll<HTMLElement>("[data-navbar-fit]"));
  for (const bar of bars) {
    // Once per bar, for the same reason as above: the canvas is rebuilt.
    if (bar.dataset.navbarFitWatched === "1") continue;
    bar.dataset.navbarFitWatched = "1";
    let queued = false;
    const again = (): void => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        measureFit(bar);
      });
    };
    measureFit(bar);
    // The canvas changes width without the window doing so (the Desktop,
    // Tablet and Mobile buttons), so the bar itself is watched, not only the
    // window.
    if (typeof ResizeObserver !== "undefined") {
      new ResizeObserver(again).observe(bar);
    }
    window.addEventListener("resize", again, { passive: true });
    // A web font arriving changes every width measured.
    document.fonts?.ready.then(again).catch(() => undefined);
  }
}

/**
 * 2.203.0. Block Navigator panels (Kelly's answer 19).
 *
 * A panel is a `details` and opens on click and Enter without this file. On a
 * computer it also opens when the pointer rests on its link -- after a short
 * pause, so a pointer crossing the bar does not flash every panel open -- and
 * closes a moment after the pointer leaves both the link and the panel. One
 * at a time: opening one closes any other open in the bar. Escape closes it
 * and gives the keyboard back to its link; a click outside closes it.
 *
 * In a List and detail panel, the item the pointer is on -- or the keyboard
 * is on -- shows its detail.
 *
 * Touch screens open it by tap, which is the `details` doing what it does:
 * only a mouse or a pen hovers.
 */
export const PANEL_OPEN_DELAY = 150;
export const PANEL_CLOSE_DELAY = 300;

/** Show the detail of item `index` in a List and detail panel. */
export function showDetail(panel: Element, index: number): void {
  panel.querySelectorAll<HTMLElement>("[data-navbar-detail]").forEach((detail) => {
    detail.hidden = Number(detail.dataset.navbarDetail) !== index;
  });
  panel.querySelectorAll<HTMLElement>("[data-navbar-item]").forEach((item) => {
    if (Number(item.dataset.navbarItem) === index) item.setAttribute("aria-current", "true");
    else item.removeAttribute("aria-current");
  });
}

/** Close every open dropdown and panel of a bar except `keep`. */
function closeOthers(bar: Element, keep: HTMLDetailsElement | null): void {
  bar.querySelectorAll<HTMLDetailsElement>(".navbar__links details.navbar__dropdown[open]")
    .forEach((open) => { if (open !== keep) open.open = false; });
}

export function bindPanels(bar: HTMLElement): void {
  if (bar.dataset.navbarPanelsWatched === "1") return;
  bar.dataset.navbarPanelsWatched = "1";

  // 2.286.0. And a dropdown list, on a bar set to open them on hover
  // (Kelly: "auto expand top links on a mouse hover"; phones still tap).
  const panels = Array.from(bar.querySelectorAll<HTMLDetailsElement>(
    bar.hasAttribute("data-navbar-hover")
      ? ".navbar__links details.navbar__dropdown"
      : ".navbar__links details.navbar__dropdown--panel"));
  for (const details of panels) {
    const item = details.parentElement ?? details;
    const summary = details.querySelector("summary");
    let opening: number | undefined;
    let closing: number | undefined;
    let openedAt = 0;

    const hovers = (event: PointerEvent) => event.pointerType === "mouse" || event.pointerType === "pen";
    const open = () => {
      closeOthers(bar, details);
      if (!details.open) {
        details.open = true;
        openedAt = Date.now();
      }
    };

    item.addEventListener("pointerenter", (event) => {
      if (!hovers(event as PointerEvent)) return;
      window.clearTimeout(closing);
      opening = window.setTimeout(open, PANEL_OPEN_DELAY);
    });
    item.addEventListener("pointerleave", (event) => {
      if (!hovers(event as PointerEvent)) return;
      window.clearTimeout(opening);
      closing = window.setTimeout(() => {
        if (!details.contains(document.activeElement)) details.open = false;
      }, PANEL_CLOSE_DELAY);
    });

    // A click just after the pointer opened it keeps it open, rather than
    // closing what the visitor is reaching for.
    summary?.addEventListener("click", (event) => {
      if (details.open && Date.now() - openedAt < 600) event.preventDefault();
    });
    details.addEventListener("toggle", () => {
      if (details.open) closeOthers(bar, details);
    });

    // List and detail: the item pointed at, or focused, shows its detail.
    details.querySelectorAll<HTMLElement>("[data-navbar-item]").forEach((link) => {
      const show = () => showDetail(details, Number(link.dataset.navbarItem));
      link.addEventListener("pointerenter", show);
      link.addEventListener("focus", show);
    });
  }

  bar.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    const open = bar.querySelector<HTMLDetailsElement>(
      ".navbar__links details.navbar__dropdown[open]");
    if (!open) return;
    open.open = false;
    open.querySelector<HTMLElement>("summary")?.focus();
  });
  document.addEventListener("pointerdown", (event) => {
    const target = event.target as Node | null;
    bar.querySelectorAll<HTMLDetailsElement>(".navbar__links details.navbar__dropdown--panel[open]")
      .forEach((open) => { if (!target || !open.contains(target)) open.open = false; });
  });
}

/**
 * 2.274.0. The bar's height as drawn, as `--navbar-drawn`, which the menu it
 * opens is placed below (editor.css). The height setting is a least: a bar
 * whose contents are taller is drawn taller, and the menu, placed at the
 * setting, opened over the bar's foot (Kelly: 27px set, 36px drawn, 9px
 * covered). Watched, because a web font arriving, the window or the canvas
 * changing width, or a row folding changes it.
 */
export function markDrawnHeight(bar: HTMLElement): void {
  bar.style.setProperty("--navbar-drawn", `${bar.offsetHeight}px`);
}

function watchBarHeights(): void {
  document.querySelectorAll<HTMLElement>("nav.navbar").forEach((bar) => {
    // Once per bar: the canvas is rebuilt, and each new bar is watched anew.
    if (bar.dataset.navbarDrawnWatched === "1") return;
    bar.dataset.navbarDrawnWatched = "1";
    markDrawnHeight(bar);
    if (typeof ResizeObserver !== "undefined") {
      new ResizeObserver(() => markDrawnHeight(bar)).observe(bar);
    }
  });
}

function watchPanels(): void {
  document.querySelectorAll<HTMLElement>("[data-navbar-panels]").forEach(bindPanels);
}

/**
 * A published page is built once and never changes, so one pass is enough./**
 * A published page is built once and never changes, so one pass is enough.
 * The builder is the opposite: its canvas is drawn by script after this file
 * runs and redrawn on every edit, so a single pass at load finds no bars at
 * all. Watching for them costs nothing where nothing ever arrives.
 */
function watchForNewBars(): void {
  const root = document.body;
  if (!root || typeof MutationObserver === "undefined") return;
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      watchBlendedBars();
      watchCrowdedBars();
      watchPanels();
      watchBarHeights();
    });
  }).observe(root, { childList: true, subtree: true });
}

function begin(): void {
  watchBlendedBars();
  watchCrowdedBars();
  watchPanels();
  watchBarHeights();
  watchForNewBars();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", begin);
} else {
  begin();
}
